import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { T, MONO } from './poyais-ui/theme';
import { ScreenHeader } from './poyais-ui/shell';
import { Icon } from './poyais-ui/icons';
import FaturalandirmaPage from './FaturalandirmaPage';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Faturalama & Harcama': 'Billing & Spend',
  'Platform verileri yükleniyor…': 'Loading platform data…',
  'Henüz bağlı bir reklam platformu yok, Ayarlar üzerinden bağlayabilirsin.': 'No ad platform connected yet — you can connect one from Settings.',
  'Toplam Harcama': 'Total Spend',
  'Platformu değiştir': 'Change platform',
  'Veri şu an alınamıyor': 'Data unavailable right now',
  'Bağlı değil': 'Not connected',
  'İşlemler': 'Transactions', 'Abonelik Faturaları': 'Subscription Invoices', 'Plan & Abonelik': 'Plan & Subscription',
  'Kampanya': 'Campaign', 'Platform': 'Platform', 'Harcama': 'Spend',
  'Yükleniyor…': 'Loading…',
  'Bu dönem için işlem bulunamadı.': 'No transactions for this period.',
  'Dönem': 'Period', 'Tutar': 'Amount', 'Durum': 'Status',
  'Poyais One aboneliğiniz için iyzico veya Stripe üzerinden yapılan tahsilatlar — Meta/Google Ads gibi reklam platformlarının kendi faturaları değil. Platform harcamalarını İşlemler sekmesinde görebilirsiniz.': "Charges for your Poyais One subscription via iyzico or Stripe — not invoices from ad platforms like Meta or Google Ads. See ad platform spend under the Transactions tab.",
  'Henüz fatura yok.': 'No invoices yet.',
  'Ödendi': 'Paid', 'Bekliyor': 'Pending', 'Tahsil Edilemedi': 'Failed',
});

const API = '/api/v1';

type PlatformSpend = {
  key: string; label: string; logo: string; metric_label: string;
  connected: boolean; value: number; error: boolean;
};
type CampaignRow = { name: string; platform_label: string; platform_logo: string; amount: number };
type Invoice = { id: string; period: string; amount: number; currency: string; status: string; invoice_pdf?: string };

const DEFAULT_CARD_KEYS = ['gads', 'meta', 'chatgpt_ads'];

function money(v: number) {
  return `₺${(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function authHeaders() {
  const tok = localStorage.getItem('poyais_token') || '';
  return { Authorization: `Bearer ${tok}` };
}

type Tab = 'islemler' | 'faturalar' | 'plan';

export default function FaturalamaPage({ config, activeClientId }: { config?: any; activeClientId?: number }) {
  const { t, lang } = useLang();
  // iyzico ödeme dönüşü (?checkout=...) doğrudan Plan & Abonelik sekmesini açar
  const [tab, setTab] = useState<Tab>(() => new URLSearchParams(window.location.search).has('checkout') ? 'plan' : 'islemler');
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState<PlatformSpend[]>([]);
  const [totalSpend, setTotalSpend] = useState(0);
  const [rows, setRows] = useState<CampaignRow[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);
  const [cardKeys, setCardKeys] = useState<string[]>(DEFAULT_CARD_KEYS);
  const [swapOpenFor, setSwapOpenFor] = useState<string | null>(null);

  const storageKey = activeClientId ? `poyais_faturalama_cards_${activeClientId}` : '';

  useEffect(() => {
    if (!storageKey) return;
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (Array.isArray(saved) && saved.length === 3) setCardKeys(saved);
      else setCardKeys(DEFAULT_CARD_KEYS);
    } catch (e) { setCardKeys(DEFAULT_CARD_KEYS); }
  }, [storageKey]);

  useEffect(() => {
    if (!activeClientId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/billing/platform-spend`, { params: { client_id: activeClientId }, headers: authHeaders() }),
      axios.get(`${API}/billing/campaigns`, { params: { client_id: activeClientId }, headers: authHeaders() }),
    ]).then(([spendRes, campRes]) => {
      if (cancelled) return;
      setPlatforms(spendRes.data.platforms || []);
      setTotalSpend(spendRes.data.total_spend || 0);
      setRows(campRes.data.rows || []);
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeClientId]);

  useEffect(() => {
    setInvoicesLoaded(false);
    axios.get(`${API}/subscription/invoices`, { params: activeClientId ? { client_id: activeClientId } : {}, headers: authHeaders() })
      .then(r => setInvoices(r.data || []))
      .catch(() => {})
      .finally(() => setInvoicesLoaded(true));
  }, [activeClientId]);

  const byKey = useMemo(() => {
    const m: Record<string, PlatformSpend> = {};
    platforms.forEach(p => { m[p.key] = p; });
    return m;
  }, [platforms]);

  const connectedAdSpendCount = platforms.filter(p => p.connected && p.metric_label === 'Harcama').length;

  const subtitle = loading
    ? t('Platform verileri yükleniyor…')
    : connectedAdSpendCount > 0
      ? (lang==='en' ? `Tracking a total of ${money(totalSpend)} spend across ${connectedAdSpendCount} platforms this period.` : `Bu dönemde ${connectedAdSpendCount} platformdan toplam ${money(totalSpend)} harcama izleniyor.`)
      : t('Henüz bağlı bir reklam platformu yok, Ayarlar üzerinden bağlayabilirsin.');

  const swapCard = (slotKey: string, newKey: string) => {
    const next = cardKeys.map(k => k === slotKey ? newKey : k);
    setCardKeys(next);
    setSwapOpenFor(null);
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <ScreenHeader title={t('Faturalama & Harcama')} subtitle={subtitle} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 14, background: T.surface, padding: '18px 19px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--py-text-mid-alt)' }}>{t('Toplam Harcama')}</span>
            <span style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--py-track-bg)', color: T.textMid,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.invoice size={15} sw={1.9} /></span>
          </div>
          <div style={{ fontSize: 27, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: MONO, color: T.text }}>
            {loading ? '—' : money(totalSpend)}
          </div>
        </div>

        {cardKeys.map(key => {
          const p = byKey[key];
          const otherOptions = platforms.filter(op => op.connected && !cardKeys.includes(op.key));
          return (
            <div key={key} style={{ border: `1px solid ${T.border}`, borderRadius: 14, background: T.surface, padding: '18px 19px', position: 'relative' as const }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--py-text-mid-alt)' }}>{p?.label || '—'}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--py-track-bg)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {p?.logo ? <img src={p.logo} width={18} height={18} style={{ objectFit: 'contain' }} alt={p.label} /> : null}
                  </span>
                  {otherOptions.length > 0 && (
                    <span onClick={() => setSwapOpenFor(swapOpenFor === key ? null : key)}
                      style={{ width: 20, height: 20, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--py-text-dim-alt)', cursor: 'pointer' }} title={t('Platformu değiştir')}>
                      <Icon.chevron size={14} sw={2} />
                    </span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 27, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: MONO,
                color: p?.connected ? T.text : T.textFaint }}>
                {loading ? '—' : (p?.connected ? money(p.value) : '₺0,00')}
              </div>
              {p && p.connected && p.error && (
                <div style={{ fontSize: 10.5, color: T.amberText, marginTop: 6, fontWeight: 600 }}>{t('Veri şu an alınamıyor')}</div>
              )}
              {p && !p.connected && (
                <div style={{ fontSize: 10.5, color: 'var(--py-text-dim-alt)', marginTop: 6, fontWeight: 600 }}>{t('Bağlı değil')}</div>
              )}

              {swapOpenFor === key && (
                <div style={{ position: 'absolute' as const, top: '100%', right: 19, marginTop: 6, background: T.surface,
                  border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: '0 8px 24px rgba(20,22,43,.12)', zIndex: 20,
                  minWidth: 180, overflow: 'hidden' }}>
                  {otherOptions.map(op => (
                    <div key={op.key} onClick={() => swapCard(key, op.key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', fontSize: 12.5,
                        fontWeight: 600, cursor: 'pointer', color: T.text }}
                      onMouseEnter={e => (e.currentTarget.style.background = T.card)}
                      onMouseLeave={e => (e.currentTarget.style.background = T.surface)}>
                      <img src={op.logo} width={16} height={16} style={{ objectFit: 'contain' }} alt={op.label} />
                      {op.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 24, borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap' as const }}>
        {([['islemler', 'İşlemler'], ['faturalar', 'Abonelik Faturaları'], ['plan', 'Plan & Abonelik']] as const).map(([key, label]) => (
          <span key={key} onClick={() => setTab(key)} style={{ padding: '0 0 12px', fontSize: 13.5, fontWeight: tab === key ? 700 : 600,
            color: tab === key ? T.text : 'var(--py-text-mid-alt)', cursor: 'pointer',
            borderBottom: tab === key ? `2px solid ${T.accent}` : 'none', marginBottom: tab === key ? -1 : 0 }}>{t(label)}</span>
        ))}
      </div>

      {tab === 'islemler' && (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 16, background: T.surface, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 0.9fr', gap: 14, padding: '13px 20px', background: 'var(--py-subtle-bg)',
            borderBottom: '1px solid var(--py-table-divider)', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' as const, color: 'var(--py-text-mid-alt)' }}>
            <span>{t('Kampanya')}</span><span>{t('Platform')}</span><span style={{ textAlign: 'right' }}>{t('Harcama')}</span>
          </div>
          {loading ? (
            <PoyaisSpinner size={36} pad={24}/>
          ) : rows.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13, color: 'var(--py-text-mid-alt)' }}>{t('Bu dönem için işlem bulunamadı.')}</div>
          ) : rows.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 0.9fr', gap: 14, padding: '15px 20px',
              borderBottom: i < rows.length - 1 ? '1px solid var(--py-page-bg)' : 'none', alignItems: 'center', fontSize: 13, color: T.text }}>
              <span style={{ fontWeight: 700 }}>{r.name}</span>
              <span style={{ color: 'var(--py-text-mid-alt)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={r.platform_logo} width={15} height={15} style={{ objectFit: 'contain' }} alt={r.platform_label} />
                {r.platform_label}
              </span>
              <span style={{ textAlign: 'right', fontWeight: 800, fontFamily: MONO }}>{money(r.amount)}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'faturalar' && (
        <div>
          <div style={{ fontSize: 12.5, color: 'var(--py-text-mid-alt)', marginBottom: 14, lineHeight: 1.5 }}>
            {t('Poyais One aboneliğiniz için iyzico veya Stripe üzerinden yapılan tahsilatlar — Meta/Google Ads gibi reklam platformlarının kendi faturaları değil. Platform harcamalarını İşlemler sekmesinde görebilirsiniz.')}
          </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 16, background: T.surface, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 14, padding: '13px 20px', background: 'var(--py-subtle-bg)',
            borderBottom: '1px solid var(--py-table-divider)', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' as const, color: 'var(--py-text-mid-alt)' }}>
            <span>{t('Dönem')}</span><span>{t('Tutar')}</span><span>{t('Durum')}</span>
          </div>
          {!invoicesLoaded ? (
            <PoyaisSpinner size={36} pad={24}/>
          ) : invoices.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13, color: 'var(--py-text-mid-alt)' }}>{t('Henüz fatura yok.')}</div>
          ) : invoices.map((r, i) => (
            <a key={i} href={r.invoice_pdf || undefined} target="_blank" rel="noreferrer"
              style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 14, padding: '15px 20px',
              borderBottom: i < invoices.length - 1 ? '1px solid var(--py-page-bg)' : 'none', alignItems: 'center', fontSize: 13,
              textDecoration: 'none', color: T.text, cursor: r.invoice_pdf ? 'pointer' : 'default' }}>
              <span style={{ fontWeight: 700 }}>{r.period}</span>
              <span style={{ fontWeight: 800, fontFamily: MONO }}>{r.currency === 'TRY' ? money(r.amount) : `${r.amount} ${r.currency}`}</span>
              <span style={{ fontSize: 11, fontWeight: 700,
                color: r.status === 'Ödendi' ? T.greenText : r.status === 'Bekliyor' ? T.amberText : T.redText,
                background: r.status === 'Ödendi' ? 'var(--py-green-tint)' : r.status === 'Bekliyor' ? 'var(--py-amber-tint-alt)' : 'var(--py-red-tint)',
                padding: '4px 10px', borderRadius: 999, display: 'inline-flex', width: 'fit-content' }}>{t(r.status)}</span>
            </a>
          ))}
        </div>
        </div>
      )}

      {tab === 'plan' && (
        <div style={{ marginTop: -4 }}>
          <FaturalandirmaPage config={config} />
        </div>
      )}
    </div>
  );
}
